import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getMediaUrl } from '../../services/api';
import GalleryGridEditor from '../GalleryGridEditor';
import useMediaFiltering from '../../hooks/useMediaFiltering';
import useCreditsTab from '../../hooks/useCreditsTab';
import useGalleryConfig from '../../hooks/useGalleryConfig';
import VisibilityToggle from './VisibilityToggle';
import './CreativeTemplate.css';

function CreativeTemplate({
  profileData,
  basicUser,
  isOwnProfile,
  onEdit,
  onTextClick,
  onColorClick,
  hiddenSections = [],
  onToggleSection,
  onPhotoClick,
  galleryEditMode,
  onToggleGalleryEdit,
  onGalleryConfigChange,
  onGalleryReorder,
  galleryConfig,
  onGalleryPhotoCrop,
}) {
  const [activeSection, setActiveSection] = useState('home');

  const { settings, performer, credits, skills, media, links } = profileData;

  // Get accent color from settings or default to vibrant pink
  const accentColor = settings?.accent_color || '#e32c74';
  const headingFont = settings?.heading_font || 'Space Grotesk';
  const bodyFont = settings?.body_font || 'Inter';
  const headingFontSize = settings?.heading_font_size || '32';
  const bodyFontSize = settings?.body_font_size || '16';

  // Profile display data
  const displayName =
    performer?.display_name || basicUser?.full_name || basicUser?.username || 'Unknown';
  const tagline =
    performer?.tagline || performer?.job_title || basicUser?.industry || 'Actor & Model';
  const bio = performer?.bio || basicUser?.bio || '';

  // Shared hooks
  const { headshots, aboutPhotos, galleryPhotos } = useMediaFiltering(media);
  const { galleryGridStyle } = useGalleryConfig(settings);
  const { activeCreditsTab, setActiveCreditsTab, creditCategories } = useCreditsTab(credits);

  // Switch to section
  const switchToSection = (sectionId) => {
    setActiveSection(sectionId);
  };

  return (
    <div
      className="creative-template"
      style={{
        '--accent-color': accentColor,
        '--heading-font': `'${headingFont}', sans-serif`,
        '--body-font': `'${bodyFont}', sans-serif`,
        '--heading-font-size': `${headingFontSize}px`,
        '--body-font-size': `${bodyFontSize}px`,
      }}
    >
      {/* Floating Pill Navigation */}
      <nav className="creative-nav">
        <Link to="/feed" className="creative-nav-back" title="Back to Feed">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
        </Link>
        <div className="creative-nav-links">
          <button
            className={activeSection === 'home' ? 'active' : ''}
            onClick={() => switchToSection('home')}
            style={
              activeSection === 'home' ? { backgroundColor: accentColor, color: '#ffffff' } : {}
            }
          >
            Home
          </button>
          {(!hiddenSections.includes('about') || onToggleSection) && (
            <button
              className={`${activeSection === 'about' ? 'active' : ''}${hiddenSections.includes('about') ? ' nav-link-hidden' : ''}`}
              onClick={() => switchToSection('about')}
              style={
                activeSection === 'about' ? { backgroundColor: accentColor, color: '#ffffff' } : {}
              }
            >
              About
            </button>
          )}
          {(!hiddenSections.includes('media') || onToggleSection) &&
          settings?.media_mode === 'separate' ? (
            <>
              <button
                className={`${activeSection === 'showreels' ? 'active' : ''}${hiddenSections.includes('media') ? ' nav-link-hidden' : ''}`}
                onClick={() => switchToSection('showreels')}
                style={
                  activeSection === 'showreels'
                    ? { backgroundColor: accentColor, color: '#ffffff' }
                    : {}
                }
              >
                Showreels
              </button>
              <button
                className={`${activeSection === 'gallery' ? 'active' : ''}${hiddenSections.includes('media') ? ' nav-link-hidden' : ''}`}
                onClick={() => switchToSection('gallery')}
                style={
                  activeSection === 'gallery'
                    ? { backgroundColor: accentColor, color: '#ffffff' }
                    : {}
                }
              >
                Gallery
              </button>
            </>
          ) : (!hiddenSections.includes('media') || onToggleSection) &&
            settings?.media_mode === 'gallery_only' ? (
            <button
              className={`${activeSection === 'gallery' ? 'active' : ''}${hiddenSections.includes('media') ? ' nav-link-hidden' : ''}`}
              onClick={() => switchToSection('gallery')}
              style={
                activeSection === 'gallery'
                  ? { backgroundColor: accentColor, color: '#ffffff' }
                  : {}
              }
            >
              Gallery
            </button>
          ) : (!hiddenSections.includes('media') || onToggleSection) &&
            settings?.media_mode === 'videos_only' ? (
            <button
              className={`${activeSection === 'showreels' ? 'active' : ''}${hiddenSections.includes('media') ? ' nav-link-hidden' : ''}`}
              onClick={() => switchToSection('showreels')}
              style={
                activeSection === 'showreels'
                  ? { backgroundColor: accentColor, color: '#ffffff' }
                  : {}
              }
            >
              Showreels
            </button>
          ) : !hiddenSections.includes('media') || onToggleSection ? (
            <button
              className={`${activeSection === 'media' ? 'active' : ''}${hiddenSections.includes('media') ? ' nav-link-hidden' : ''}`}
              onClick={() => switchToSection('media')}
              style={
                activeSection === 'media' ? { backgroundColor: accentColor, color: '#ffffff' } : {}
              }
            >
              Media
            </button>
          ) : null}
          {creditCategories.length > 0 &&
            (!hiddenSections.includes('resume') || onToggleSection) && (
              <button
                className={`${activeSection === 'resume' ? 'active' : ''}${hiddenSections.includes('resume') ? ' nav-link-hidden' : ''}`}
                onClick={() => switchToSection('resume')}
                style={
                  activeSection === 'resume'
                    ? { backgroundColor: accentColor, color: '#ffffff' }
                    : {}
                }
              >
                Resume
              </button>
            )}
          {(!hiddenSections.includes('contact') || onToggleSection) && (
            <button
              className={`${activeSection === 'contact' ? 'active' : ''}${hiddenSections.includes('contact') ? ' nav-link-hidden' : ''}`}
              onClick={() => switchToSection('contact')}
              style={
                activeSection === 'contact'
                  ? { backgroundColor: accentColor, color: '#ffffff' }
                  : {}
              }
            >
              Contact
            </button>
          )}
        </div>
        {isOwnProfile && (
          <button
            className="creative-nav-edit"
            onClick={onEdit}
            style={{ backgroundColor: accentColor }}
          >
            Edit Profile
          </button>
        )}
      </nav>

      {/* Hero Section */}
      {activeSection === 'home' && (
        <section className="creative-hero">
          <div className="creative-hero-bg">
            <div className="creative-hero-dots"></div>
            <div className="creative-hero-circle creative-hero-circle-1"></div>
            <div className="creative-hero-circle creative-hero-circle-2"></div>
            <div className="creative-hero-circle creative-hero-circle-3"></div>
          </div>
          <div className="creative-hero-content">
            <div className="creative-hero-photo">
              {headshots.length > 0 ? (
                <img
                  src={getMediaUrl(headshots[0].url)}
                  alt={displayName}
                  style={{
                    objectPosition: `${headshots[0].position_x ?? 50}% ${headshots[0].position_y ?? 50}%`,
                    transform:
                      (headshots[0].zoom ?? 100) > 100
                        ? `scale(${(headshots[0].zoom ?? 100) / 100})`
                        : undefined,
                    transformOrigin: `${headshots[0].position_x ?? 50}% ${headshots[0].position_y ?? 50}%`,
                  }}
                />
              ) : basicUser?.profile_image ? (
                <img src={basicUser.profile_image} alt={displayName} />
              ) : (
                <div className="creative-hero-photo-placeholder">
                  <span>{displayName.charAt(0).toUpperCase()}</span>
                </div>
              )}
              {isOwnProfile && onPhotoClick && (
                <div className="photo-edit-overlay" onClick={() => onPhotoClick('headshot')}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  <span>Change</span>
                </div>
              )}
            </div>
            <h1
              className={`creative-hero-name${onTextClick ? ' showcase-editable' : ''}`}
              onClick={
                onTextClick
                  ? (e) => {
                      e.stopPropagation();
                      onTextClick('heading', e);
                    }
                  : undefined
              }
            >
              {displayName}
            </h1>
            <p
              className={`creative-hero-tagline${onTextClick ? ' showcase-editable' : ''}`}
              onClick={
                onTextClick
                  ? (e) => {
                      e.stopPropagation();
                      onTextClick('body', e);
                    }
                  : undefined
              }
            >
              {tagline}
            </p>
            {skills && skills.length > 0 && (
              <div className="creative-hero-skills">
                {skills.slice(0, 5).map((skill, index) => (
                  <span key={index} className="creative-skill-pill">
                    {skill.name || skill}
                  </span>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* About Section */}
      {activeSection === 'about' && (!hiddenSections.includes('about') || onToggleSection) && (
        <section className="creative-about">
          <div className="creative-about-inner">
            <div className="creative-about-photo-wrap">
              {aboutPhotos.length > 0 ? (
                <>
                  <img
                    src={getMediaUrl(aboutPhotos[0].url)}
                    alt={displayName}
                    className="creative-about-photo"
                    style={{
                      objectPosition: `${aboutPhotos[0].position_x ?? 50}% ${aboutPhotos[0].position_y ?? 50}%`,
                      transform:
                        (aboutPhotos[0].zoom ?? 100) > 100
                          ? `scale(${(aboutPhotos[0].zoom ?? 100) / 100})`
                          : undefined,
                      transformOrigin: `${aboutPhotos[0].position_x ?? 50}% ${aboutPhotos[0].position_y ?? 50}%`,
                    }}
                  />
                  {isOwnProfile && onPhotoClick && (
                    <div className="photo-edit-overlay" onClick={() => onPhotoClick('about')}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                        <circle cx="12" cy="13" r="4" />
                      </svg>
                      <span>Change</span>
                    </div>
                  )}
                </>
              ) : basicUser?.profile_image ? (
                <img
                  src={basicUser.profile_image}
                  alt={displayName}
                  className="creative-about-photo"
                />
              ) : (
                <div className="creative-about-photo creative-about-photo-fallback">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="creative-about-block">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  maxWidth: '900px',
                  margin: '0 auto',
                  width: '100%',
                }}
              >
                <h2
                  className={`creative-section-title${onTextClick ? ' showcase-editable' : ''}`}
                  onClick={
                    onTextClick
                      ? (e) => {
                          e.stopPropagation();
                          onTextClick('heading', e);
                        }
                      : undefined
                  }
                >
                  About Me
                </h2>
                <VisibilityToggle
                  section="about"
                  hiddenSections={hiddenSections}
                  onToggleSection={onToggleSection}
                />
              </div>
              <div
                className={`creative-about-text${onTextClick ? ' showcase-editable' : ''}`}
                onClick={
                  onTextClick
                    ? (e) => {
                        e.stopPropagation();
                        onTextClick('body', e);
                      }
                    : undefined
                }
              >
                {bio ? (
                  bio.split('\n\n').map((paragraph, index) => <p key={index}>{paragraph}</p>)
                ) : (
                  <p>Add your bio in the profile settings to tell your story.</p>
                )}
              </div>
              {creditCategories.length > 0 && (
                <button
                  className="creative-cta-btn"
                  onClick={() => switchToSection('resume')}
                  style={{ backgroundColor: accentColor }}
                >
                  View My Resume
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Separate mode: Showreels Section */}
      {activeSection === 'showreels' && (!hiddenSections.includes('media') || onToggleSection) && (
        <section className="creative-showreels">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              maxWidth: '900px',
              margin: '0 auto',
              width: '100%',
            }}
          >
            <h2 className="creative-section-title creative-section-title-center">Showreels</h2>
            <VisibilityToggle
              section="media"
              hiddenSections={hiddenSections}
              onToggleSection={onToggleSection}
            />
          </div>
          {media && media.filter((m) => m.media_type === 'video').length > 0 ? (
            <div className="creative-showreels-grid">
              {media
                .filter((m) => m.media_type === 'video')
                .map((item, index) => (
                  <div key={index} className="creative-video-card">
                    {item.embed_url ? (
                      <div className="creative-video-embed">
                        <iframe
                          src={item.embed_url}
                          title={item.title || 'Video'}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    ) : (
                      <div className="creative-video-placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    )}
                    {item.title && <p className="creative-video-title">{item.title}</p>}
                  </div>
                ))}
            </div>
          ) : (
            <div style={{ padding: '60px 40px', textAlign: 'center', color: '#999' }}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                style={{ marginBottom: '16px', opacity: 0.5 }}
              >
                <polygon points="23 7 16 12 23 17 23 7" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
              <p style={{ fontSize: '16px', margin: 0 }}>
                No videos added yet. Add videos in profile settings.
              </p>
            </div>
          )}
        </section>
      )}

      {/* Separate mode: Gallery Section */}
      {activeSection === 'gallery' && (!hiddenSections.includes('media') || onToggleSection) && (
        <section className="creative-gallery" style={{ position: 'relative' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              maxWidth: '900px',
              margin: '0 auto',
              width: '100%',
            }}
          >
            <h2 className="creative-section-title creative-section-title-center">Gallery</h2>
            <VisibilityToggle
              section="media"
              hiddenSections={hiddenSections}
              onToggleSection={onToggleSection}
            />
          </div>
          {isOwnProfile && onToggleGalleryEdit && (
            <button
              className={`gallery-edit-btn${galleryEditMode ? ' active' : ''}`}
              onClick={onToggleGalleryEdit}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
              </svg>
              {galleryEditMode ? 'Done Editing' : 'Edit Gallery Layout'}
            </button>
          )}
          {galleryEditMode && onGalleryConfigChange ? (
            <GalleryGridEditor
              photos={galleryPhotos}
              config={galleryConfig}
              onChange={onGalleryConfigChange}
              onReorder={onGalleryReorder}
              onCropPhoto={onGalleryPhotoCrop}
              theme="creative"
            />
          ) : (
            <div className="creative-gallery-grid" style={galleryGridStyle}>
              {galleryPhotos.length > 0
                ? galleryPhotos.map((item) => (
                    <div key={item.id} className="creative-gallery-item">
                      <img
                        src={getMediaUrl(item.url)}
                        alt={item.title || 'Gallery image'}
                        style={{
                          objectPosition: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                          transform:
                            (item.zoom ?? 100) > 100
                              ? `scale(${(item.zoom ?? 100) / 100})`
                              : undefined,
                          transformOrigin: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                        }}
                      />
                      <div className="creative-gallery-overlay">
                        {item.title && <h3>{item.title}</h3>}
                      </div>
                      {isOwnProfile && onGalleryPhotoCrop && (
                        <div
                          className="gallery-photo-crop-overlay"
                          onClick={(e) => onGalleryPhotoCrop(item, e)}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" />
                            <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" />
                          </svg>
                          <span>Crop</span>
                        </div>
                      )}
                    </div>
                  ))
                : Array.from({ length: 6 }).map((_, index) => (
                    <div
                      key={index}
                      className="creative-gallery-item creative-gallery-placeholder-item"
                    >
                      <div className="creative-gallery-placeholder-content">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                        </svg>
                        <p>Add photos</p>
                      </div>
                    </div>
                  ))}
            </div>
          )}
        </section>
      )}

      {/* Together mode: Combined Media Section */}
      {activeSection === 'media' && (!hiddenSections.includes('media') || onToggleSection) && (
        <section className="creative-gallery" style={{ position: 'relative' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              maxWidth: '900px',
              margin: '0 auto',
              width: '100%',
            }}
          >
            <h2 className="creative-section-title creative-section-title-center">Media</h2>
            <VisibilityToggle
              section="media"
              hiddenSections={hiddenSections}
              onToggleSection={onToggleSection}
            />
          </div>
          {isOwnProfile && onToggleGalleryEdit && (
            <button
              className={`gallery-edit-btn${galleryEditMode ? ' active' : ''}`}
              onClick={onToggleGalleryEdit}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
              </svg>
              {galleryEditMode ? 'Done Editing' : 'Edit Gallery Layout'}
            </button>
          )}
          {media && media.filter((m) => m.media_type === 'video').length > 0 && (
            <div className="creative-showreels-grid" style={{ marginBottom: '40px' }}>
              {media
                .filter((m) => m.media_type === 'video')
                .map((item, index) => (
                  <div key={index} className="creative-video-card">
                    {item.embed_url ? (
                      <div className="creative-video-embed">
                        <iframe
                          src={item.embed_url}
                          title={item.title || 'Video'}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    ) : (
                      <div className="creative-video-placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    )}
                    {item.title && <p className="creative-video-title">{item.title}</p>}
                  </div>
                ))}
            </div>
          )}
          {galleryEditMode && onGalleryConfigChange ? (
            <GalleryGridEditor
              photos={galleryPhotos}
              config={galleryConfig}
              onChange={onGalleryConfigChange}
              onReorder={onGalleryReorder}
              onCropPhoto={onGalleryPhotoCrop}
              theme="creative"
            />
          ) : (
            <div className="creative-gallery-grid" style={galleryGridStyle}>
              {galleryPhotos.length > 0
                ? galleryPhotos.map((item) => (
                    <div key={item.id} className="creative-gallery-item">
                      <img
                        src={getMediaUrl(item.url)}
                        alt={item.title || 'Gallery image'}
                        style={{
                          objectPosition: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                          transform:
                            (item.zoom ?? 100) > 100
                              ? `scale(${(item.zoom ?? 100) / 100})`
                              : undefined,
                          transformOrigin: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                        }}
                      />
                      <div className="creative-gallery-overlay">
                        {item.title && <h3>{item.title}</h3>}
                      </div>
                      {isOwnProfile && onGalleryPhotoCrop && (
                        <div
                          className="gallery-photo-crop-overlay"
                          onClick={(e) => onGalleryPhotoCrop(item, e)}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" />
                            <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" />
                          </svg>
                          <span>Crop</span>
                        </div>
                      )}
                    </div>
                  ))
                : Array.from({ length: 6 }).map((_, index) => (
                    <div
                      key={index}
                      className="creative-gallery-item creative-gallery-placeholder-item"
                    >
                      <div className="creative-gallery-placeholder-content">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                        </svg>
                        <p>Add photos</p>
                      </div>
                    </div>
                  ))}
            </div>
          )}
        </section>
      )}

      {/* Resume Section */}
      {activeSection === 'resume' &&
        (!hiddenSections.includes('resume') || onToggleSection) &&
        creditCategories.length > 0 && (
          <section className="creative-resume">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                maxWidth: '900px',
                margin: '0 auto',
                width: '100%',
              }}
            >
              <h2 className="creative-section-title creative-section-title-center">Resume</h2>
              <VisibilityToggle
                section="resume"
                hiddenSections={hiddenSections}
                onToggleSection={onToggleSection}
              />
            </div>

            <div className="creative-credits-tabs">
              {creditCategories.map((category) => (
                <button
                  key={category}
                  className={`creative-credits-tab ${activeCreditsTab === category ? 'active' : ''}`}
                  onClick={() => setActiveCreditsTab(category)}
                  style={
                    activeCreditsTab === category
                      ? { backgroundColor: accentColor, color: '#ffffff' }
                      : {}
                  }
                >
                  {category}
                </button>
              ))}
            </div>

            {activeCreditsTab && credits[activeCreditsTab] && (
              <div className="creative-credits-grid">
                {credits[activeCreditsTab].map((credit, index) => (
                  <div
                    key={index}
                    className="creative-credit-card"
                    style={{ borderLeftColor: accentColor }}
                  >
                    <h4 className="creative-credit-production">
                      <Link
                        to={`/production?name=${encodeURIComponent(credit.production)}${credit.company ? `&company=${encodeURIComponent(credit.company)}` : ''}`}
                        className="credit-production-link"
                      >
                        {credit.production}
                      </Link>
                    </h4>
                    <p className="creative-credit-role">{credit.role}</p>
                    {credit.company && <p className="creative-credit-detail">{credit.company}</p>}
                    {credit.director && (
                      <p className="creative-credit-detail">Dir. {credit.director}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {performer?.resume_pdf_url && (
              <div className="creative-resume-download">
                <a
                  href={getMediaUrl(performer.resume_pdf_url)}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="creative-cta-btn"
                  style={{ backgroundColor: accentColor }}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                  </svg>
                  Download Resume PDF
                </a>
              </div>
            )}
          </section>
        )}

      {/* Contact Section */}
      {activeSection === 'contact' && (!hiddenSections.includes('contact') || onToggleSection) && (
        <section
          className={`creative-contact${onColorClick ? ' showcase-editable-color' : ''}`}
          style={{ backgroundColor: accentColor }}
          onClick={
            onColorClick
              ? (e) => {
                  if (e.target === e.currentTarget || e.target.closest('.creative-contact-inner')) {
                    e.stopPropagation();
                    onColorClick(e);
                  }
                }
              : undefined
          }
        >
          <div className="creative-contact-inner">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                maxWidth: '900px',
                margin: '0 auto',
                width: '100%',
              }}
            >
              <h2 className="creative-contact-heading">Contact</h2>
              <VisibilityToggle
                section="contact"
                hiddenSections={hiddenSections}
                onToggleSection={onToggleSection}
              />
            </div>

            {performer?.contact_blurb && (
              <p className="creative-contact-blurb">{performer.contact_blurb}</p>
            )}
            <div className="creative-contact-details">
              {performer?.representation && (
                <div className="creative-contact-item">
                  <span className="creative-contact-item-label">Representation</span>
                  <span>{performer.representation}</span>
                </div>
              )}
              {basicUser?.location && (
                <div className="creative-contact-item">
                  <span className="creative-contact-item-label">Location</span>
                  <span>{basicUser.location}</span>
                </div>
              )}
              {links &&
                links
                  .filter((l) => l.is_public !== 0)
                  .map((link, index) => (
                    <div key={index} className="creative-contact-item">
                      <span className="creative-contact-item-label">{link.label}</span>
                      <span>
                        {link.url.startsWith('http') ? (
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'inherit' }}
                          >
                            {link.url.replace(/^https?:\/\/(www\.)?/, '')}
                          </a>
                        ) : (
                          link.url
                        )}
                      </span>
                    </div>
                  ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default CreativeTemplate;
